import {
  DEFAULT_SKU_PICKS,
  MAX_SKU_PICKS,
  skuKeyLabel,
  type SkuSeriesItem,
} from "@estelle/domain";
import { X } from "lucide-react";
import { useId, useMemo, useState, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCount } from "@/lib/chart-format";
import {
  matchesSearch,
  pickColour,
  skuLabel,
  skuProduct,
} from "@/modules/reports/skuTrends";

/** How many matches the list shows at once; the rest are one more letter away. */
const SHOWN = 8;

/**
 * Which products the charts draw (#678): search by SKU or product name, add as
 * many as the chart can hold, remove any. Left alone it draws the best sellers
 * in the dates, which is what a first look wants.
 *
 * What it offers are the SKUs that sold in the dates on screen, most units
 * first, so opening it unsearched is a list of what is worth drawing. A SKU
 * picked and then left without sales by a change of dates stays in the chips,
 * as a line of zeros, rather than vanishing from under the person who chose it.
 */
export function ProductPicker({
  items,
  picked,
  isDefault,
  onChange,
  onReset,
}: {
  /** Every SKU that sold in the dates, most units first. */
  items: readonly SkuSeriesItem[];
  /** The keys drawn now, in order. */
  picked: readonly string[];
  /** Whether `picked` is the best sellers rather than a choice. */
  isDefault: boolean;
  onChange: (keys: string[]) => void;
  onReset: () => void;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const byKey = useMemo(
    () => new Map(items.map((item) => [item.key, item] as const)),
    [items],
  );
  const full = picked.length >= MAX_SKU_PICKS;

  const matches = useMemo(() => {
    const chosen = new Set(picked);
    return items
      .filter((item) => !chosen.has(item.key) && matchesSearch(item, text))
      .slice(0, SHOWN);
  }, [items, picked, text]);

  const showList = open && !full;
  const current = Math.min(active, Math.max(0, matches.length - 1));

  function add(item: SkuSeriesItem) {
    onChange([...picked, item.key]);
    setText("");
    setActive(0);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive(Math.min(current + 1, matches.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      const choice = matches[current];
      if (choice && showList) {
        event.preventDefault();
        add(choice);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    } else if (event.key === "Backspace" && text === "" && picked.length > 0) {
      onChange(picked.slice(0, -1));
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={`${id}-search`} className="text-xs">
          Products on the charts
        </Label>
        <span className="text-muted-foreground text-xs">
          {isDefault
            ? `Showing the ${String(DEFAULT_SKU_PICKS)} best sellers in these dates`
            : `${String(picked.length)} of ${String(MAX_SKU_PICKS)} chosen`}
        </span>
      </div>

      <div className="relative">
        <div className="border-input focus-within:ring-ring flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5 focus-within:ring-2">
          {picked.map((key, index) => {
            const item = byKey.get(key);
            const name = item ? skuLabel(item) : skuKeyLabel(key);
            return (
              <span
                key={key}
                title={item ? skuProduct(item) : "No sales in these dates"}
                className="bg-secondary text-secondary-foreground flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs"
              >
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: pickColour(index) }}
                />
                <span className="max-w-48 truncate">{name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${name} from the charts`}
                  onClick={() => {
                    onChange(picked.filter((other) => other !== key));
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            );
          })}
          <Input
            id={`${id}-search`}
            role="combobox"
            aria-expanded={showList}
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            autoComplete="off"
            value={text}
            disabled={full}
            onChange={(event) => {
              setText(event.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
            }}
            onBlur={() => {
              setOpen(false);
            }}
            onKeyDown={onKeyDown}
            placeholder={
              full
                ? `Up to ${String(MAX_SKU_PICKS)} at once — remove one to add another`
                : "Search by SKU or product name"
            }
            className="h-6 min-w-40 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
          />
        </div>

        {showList ? (
          <ul
            id={`${id}-list`}
            role="listbox"
            aria-label="Products that sold in these dates"
            className="bg-popover text-popover-foreground absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border p-1 shadow-md"
          >
            {matches.length === 0 ? (
              <li className="text-muted-foreground px-2 py-2 text-sm">
                {text.trim()
                  ? `No product matching “${text.trim()}” sold in these dates.`
                  : "Every product that sold is already on the charts."}
              </li>
            ) : (
              matches.map((item, index) => (
                <li
                  key={item.key}
                  role="option"
                  aria-selected={index === current}
                  // The input keeps focus: a click that took it would close the
                  // list before the click landed.
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    add(item);
                  }}
                  onMouseEnter={() => {
                    setActive(index);
                  }}
                  className="aria-selected:bg-accent flex cursor-pointer items-baseline justify-between gap-3 rounded-sm px-2 py-1.5 text-sm"
                >
                  <span className="min-w-0">
                    <span className="font-medium">{item.sku ?? "No SKU"}</span>
                    <span className="text-muted-foreground ml-2 truncate">
                      {skuProduct(item)}
                    </span>
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    {formatCount(item.units)} sold
                  </span>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>

      {!isDefault ? (
        <div>
          <Button type="button" variant="ghost" size="xs" onClick={onReset}>
            Back to the {DEFAULT_SKU_PICKS} best sellers
          </Button>
        </div>
      ) : null}
    </div>
  );
}
